/**
 * Projekt-Controlling: eine Zeile je Projekt, alle Werte aus dem Modell.
 *
 * Die Spalten trennen Zeitraum (Erbracht, Abgerechnet) von Stichtag (Noch
 * abzurechnen, Auftragsbestand) — frueher stand beides unter derselben
 * Beschriftung nebeneinander (Befund 5).
 */
import React, { useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import type { CockpitModel, ProjectRow } from '../../services/cockpitTypes';
import { Panel } from './CockpitBits';
import { fmtCents, statusChip } from './cockpitUi';

type SortKey = 'contract' | 'performed' | 'billed' | 'unbilled' | 'backlog' | 'remaining';

const TH = 'px-3 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase whitespace-nowrap';
const TD = 'px-3 py-2.5 text-xs';

export interface ProjectsTabProps {
    model: CockpitModel;
    numbers: Record<string, string>;
    hideAmounts: boolean;
    onSelect: (project: ProjectRow) => void;
}

export const ProjectsTab: React.FC<ProjectsTabProps> = ({ model, numbers, hideAmounts, onSelect }) => {
    const [sort, setSort] = useState<SortKey>('contract');

    const rows = useMemo(() => {
        const value = (p: ProjectRow): number => {
            switch (sort) {
                case 'performed': return p.performedCents;
                case 'billed': return p.billedCents;
                case 'unbilled': return p.unbilledCents;
                case 'backlog': return p.backlogCents;
                case 'remaining': return -(p.daysRemaining ?? 99999);
                default: return p.contractCents;
            }
        };
        return [...model.projects].sort((a, b) => value(b) - value(a));
    }, [model.projects, sort]);

    const head = (label: string, key: SortKey) => (
        <th className={`${TH} text-right cursor-pointer ${sort === key ? 'text-blue-600' : ''}`} onClick={() => setSort(key)}>
            {label}
        </th>
    );

    return (
        <Panel
            title="Projekt-Controlling"
            icon={<Building2 className="w-4 h-4 text-indigo-500" />}
            right={<span className="text-[11px] text-slate-400">{rows.length} Projekte</span>}
            bodyClassName="overflow-x-auto"
        >
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-slate-100">
                        <th className={`${TH} pl-5`}>Projekt</th>
                        <th className={TH}>Status</th>
                        <th className={TH}>Verantwortlich</th>
                        <th className={TH}>Auftraggeber</th>
                        {head('Vertragswert', 'contract')}
                        {head('Erbracht', 'performed')}
                        {head('Abgerechnet', 'billed')}
                        {head('Noch abzur.', 'unbilled')}
                        {head('Auftragsbestand', 'backlog')}
                        {head('Restlaufzeit', 'remaining')}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {rows.map((p) => {
                        const chip = statusChip(p.status);
                        const days = p.daysRemaining;
                        return (
                            <tr key={p.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onSelect(p)}>
                                <td className="px-5 py-2.5">
                                    <div className="text-xs font-semibold text-slate-700">{p.name}</div>
                                    <div className="text-[11px] text-slate-400 font-mono">#{numbers[p.id] ?? '—'}</div>
                                </td>
                                <td className={TD}>
                                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${chip.bg} ${chip.text} ${chip.border}`}>{chip.label}</span>
                                </td>
                                <td className={`${TD} text-slate-600`}>{p.responsibleName}</td>
                                <td className={`${TD} text-slate-500 max-w-[150px] truncate`}>{p.customerName}</td>
                                <td className={`${TD} text-right font-semibold text-slate-700`}>{fmtCents(p.contractCents, hideAmounts)}</td>
                                <td className={`${TD} text-right font-semibold text-emerald-700`}>{fmtCents(p.performedCents, hideAmounts)}</td>
                                <td className={`${TD} text-right text-blue-700`}>{fmtCents(p.billedCents, hideAmounts)}</td>
                                <td className={`${TD} text-right text-amber-600`}>{fmtCents(p.unbilledCents, hideAmounts)}</td>
                                <td className={`${TD} text-right text-slate-600`}>{fmtCents(p.backlogCents, hideAmounts)}</td>
                                <td className={`${TD} text-right`}>
                                    {days === null
                                        ? <span className="text-[11px] text-slate-300">—</span>
                                        : <span className={`text-[11px] font-semibold ${days <= 30 ? 'text-red-500' : days <= 60 ? 'text-amber-500' : 'text-slate-500'}`}>{days}d</span>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Panel>
    );
};
